import React, { useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { message } from 'antd';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbIcon,
  WxbKpiCard,
  WxbModal,
  WxbSpinner,
  WxbTag,
  WxbUpload,
} from '../wxb-ui';
import {
  processTemplateV2Api,
  type ResourceNodeImportResult,
  type ResourceNodeImportRow,
} from '../../services/processTemplateV2Api';
import { resourceNodeEquipmentCatalogApi } from '../../services/resourceNodeEquipmentCatalogApi';
import {
  downloadResourceNodeTemplate,
  MAX_RESOURCE_NODE_IMPORT_FILE_BYTES,
} from './resourceNodeExcel';
import './EquipmentImportModal.css';

interface EquipmentImportModalProps {
  open: boolean;
  canWrite: boolean;
  onClose: () => void;
  onImported: () => Promise<void> | void;
}

const statusMeta: Record<ResourceNodeImportRow['status'], { label: string; color: 'blue' | 'green' | 'amber' | 'red' }> = {
  create: { label: '待新增', color: 'blue' },
  skip: { label: '自动跳过', color: 'amber' },
  error: { label: '需修正', color: 'red' },
  created: { label: '已新增', color: 'green' },
  skipped: { label: '已跳过', color: 'amber' },
};

const nodeClassLabel: Record<string, string> = {
  SITE: '厂区',
  LINE: '产线',
  ROOM: '房间',
  EQUIPMENT_UNIT: '设备',
  COMPONENT: '组件',
  UTILITY_STATION: '工作站',
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const candidate = error as {
    response?: {
      data?: {
        error?: string;
      };
    };
    message?: string;
  };
  return candidate.response?.data?.error || candidate.message || fallback;
};

const getImportResultFromError = (error: unknown): ResourceNodeImportResult | null => {
  const candidate = error as {
    response?: {
      data?: {
        data?: ResourceNodeImportResult;
      };
    };
  };
  return candidate.response?.data?.data ?? null;
};

const EquipmentImportModal: React.FC<EquipmentImportModalProps> = ({
  open,
  canWrite,
  onClose,
  onImported,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ResourceNodeImportResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setErrorMessage(null);
  };

  const close = () => {
    if (previewing || importing) return;
    reset();
    onClose();
  };

  const runPreview = async (selected: File) => {
    setPreviewing(true);
    setErrorMessage(null);
    setPreview(null);
    try {
      setPreview(await processTemplateV2Api.previewResourceNodeImport(selected));
    } catch (error) {
      setErrorMessage(getErrorMessage(error, '预检失败，请检查文件后重试'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    const selected = files[0];
    if (!selected || previewing || importing) return;
    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      setFile(null);
      setPreview(null);
      setErrorMessage('只接受 .xlsx 文件，请使用系统下载的模板。');
      return;
    }
    if (selected.size > MAX_RESOURCE_NODE_IMPORT_FILE_BYTES) {
      setFile(null);
      setPreview(null);
      setErrorMessage('文件不能超过 5 MB。');
      return;
    }
    setFile(selected);
    await runPreview(selected);
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    setErrorMessage(null);
    try {
      const catalog = await resourceNodeEquipmentCatalogApi.getCatalog();
      await downloadResourceNodeTemplate(catalog);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, '模板生成失败，请稍后重试'));
    } finally {
      setDownloading(false);
    }
  };

  const confirmImport = async () => {
    if (!file || !preview?.can_import || importing || !canWrite) return;
    setImporting(true);
    setErrorMessage(null);
    try {
      const result = await processTemplateV2Api.importResourceNodes(file);
      message.success(`导入完成：新增 ${result.summary.created ?? 0} 条，跳过 ${result.summary.skipped ?? 0} 条`);
      await onImported();
      reset();
      onClose();
    } catch (error) {
      const latestPreview = getImportResultFromError(error);
      if (latestPreview) setPreview(latestPreview);
      setErrorMessage(getErrorMessage(error, '导入失败，请重新预检后再试'));
    } finally {
      setImporting(false);
    }
  };

  const columns = useMemo<ColumnsType<ResourceNodeImportRow>>(() => [
    {
      title: 'Excel 行',
      dataIndex: 'row',
      width: 82,
    },
    {
      title: '节点',
      key: 'node',
      width: 250,
      render: (_, row) => (
        <div className="equip-import-node-cell">
          <strong>{row.nodeName || '未填写名称'}</strong>
          <span>{row.nodeCode || '未填写编码'}</span>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'nodeClass',
      width: 110,
      render: (value: string) => nodeClassLabel[value] ?? value ?? '—',
    },
    {
      title: '上级节点编码',
      dataIndex: 'parentCode',
      width: 180,
      render: (value: string | null) => value || '—',
    },
    {
      title: '结果',
      dataIndex: 'status',
      width: 110,
      render: (value: ResourceNodeImportRow['status']) => (
        <WxbTag color={statusMeta[value].color}>{statusMeta[value].label}</WxbTag>
      ),
    },
    {
      title: '处理说明',
      key: 'issues',
      render: (_, row) => (
        row.errors.length > 0
          ? <span className="equip-import-error-text">{row.errors.join('；')}</span>
          : <span className="equip-import-muted-text">{row.skipReason || '校验通过'}</span>
      ),
    },
  ], []);

  const footer = (
    <div className="equip-import-footer">
      <WxbButton type="button" variant="ghost" onClick={close} disabled={previewing || importing}>
        取消
      </WxbButton>
      <WxbButton
        type="button"
        variant="primary"
        disabled={!canWrite || !file || !preview?.can_import || previewing || importing}
        onClick={confirmImport}
      >
        {importing ? '正在导入…' : '确认导入'}
      </WxbButton>
    </div>
  );

  return (
    <WxbModal
      title="批量导入资源节点"
      open={open}
      width={980}
      footer={footer}
      maskClosable={!previewing && !importing}
      closable={!previewing && !importing}
      onCancel={close}
      className="equip-import-modal"
    >
      <div className="equip-import-body">
        <WxbAlert title="系统会先预检，不会直接写入">
          仅新增节点；已有编码自动跳过。只要有一行错误，整批数据都不会写入，修正后重新选择文件即可。
        </WxbAlert>

        <section className="equip-import-step">
          <div className="equip-import-step-heading">
            <div>
              <h3>1. 下载并填写模板</h3>
              <p>模板用颜色区分必填和条件必填字段；“模板字典”只包含可选枚举和人工维护的可复用设备型号，不会带入已有设备。</p>
            </div>
            <WxbButton type="button" variant="secondary" onClick={downloadTemplate} disabled={downloading}>
              {downloading ? '生成中…' : '下载 XLSX 模板'}
            </WxbButton>
          </div>
        </section>

        <section className="equip-import-step">
          <div className="equip-import-step-heading">
            <div>
              <h3>2. 上传并自动预检</h3>
              <p>仅接受不超过 5 MB 的 .xlsx 文件，最多 1000 条节点数据。</p>
            </div>
            {file && (
              <WxbButton type="button" variant="ghost" size="sm" onClick={reset} disabled={previewing || importing}>
                清除文件
              </WxbButton>
            )}
          </div>

          <WxbUpload
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple={false}
            onFilesSelected={handleFilesSelected}
            fileList={file ? [{
              uid: `${file.name}-${file.lastModified}`,
              name: file.name,
              size: file.size,
              status: previewing ? 'uploading' : preview?.can_import ? 'done' : 'error',
            }] : []}
            className="equip-import-upload"
          >
            <div className="equip-import-upload-content">
              <WxbIcon name="batch-record" size={34} aria-hidden="true" />
              <strong>{file ? '选择其他 XLSX 文件' : '选择或拖入 XLSX 文件'}</strong>
              <span>上传后自动执行结构、层级、设备字典和重复编码校验</span>
            </div>
          </WxbUpload>
        </section>

        {previewing && <WxbSpinner tip="正在预检工作簿…" />}

        {errorMessage && (
          <WxbAlert variant="error" title="文件未通过检查">
            {errorMessage}
          </WxbAlert>
        )}

        {preview && !previewing && (
          <section className="equip-import-step" aria-labelledby="equip-import-preview-title">
            <div className="equip-import-step-heading">
              <div>
                <h3 id="equip-import-preview-title">3. 确认预检结果</h3>
                <p>Excel 行号与下表一致，可直接回到原文件定位并修正。</p>
              </div>
              <WxbButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => file && runPreview(file)}
                disabled={previewing || importing}
              >
                重新预检
              </WxbButton>
            </div>

            <div className="equip-import-summary">
              <WxbKpiCard title="数据行" value={preview.summary.total} />
              <WxbKpiCard title="待新增" value={preview.summary.toCreate ?? 0} />
              <WxbKpiCard title="自动跳过" value={preview.summary.toSkip ?? 0} />
              <WxbKpiCard title="错误行" value={preview.summary.errors ?? 0} />
            </div>

            <WxbAlert
              variant={preview.can_import ? 'warning' : 'error'}
              title={preview.can_import ? '预检通过，可以确认导入' : '预检未通过，请先修正错误行'}
            >
              {preview.can_import
                ? `将新增 ${preview.summary.toCreate ?? 0} 条，跳过 ${preview.summary.toSkip ?? 0} 条已有节点。`
                : `共有 ${preview.summary.errors ?? 0} 行阻断错误，当前不会写入任何数据。`}
            </WxbAlert>

            <WxbDataTable<ResourceNodeImportRow>
              rowKey={(row) => `${row.row}-${row.nodeCode}`}
              density="compact"
              columns={columns}
              dataSource={preview.rows}
              pagination={preview.rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
              scroll={{ x: 980, y: 330 }}
            />
          </section>
        )}
      </div>
    </WxbModal>
  );
};

export default EquipmentImportModal;
